/**
 * 주간 보고 마감본 PDF. A4 가로, 회색조만 쓴다. 글자는 한국어 서브셋 글꼴로 그린다.
 */
import { grayscale, PDFDocument, type PDFFont, type PDFPage } from "pdf-lib";
import { COST_KPI, UNAVAILABLE_COST, costKpiText } from "@/features/pm/cost-evm";
import { suggestPeriodName } from "@/features/reports/report-model";
import type { WeeklyV2WeekSnapshot } from "./weekly-report-v2-data";

function sheetTitle(report: WeeklyV2WeekSnapshot): string {
  if (report.title?.trim()) return report.title.trim();
  if (/^\d{1,2}월 \d+주차 주간 보고$/.test(report.weekLabel)) return report.weekLabel;
  if (/^\d{4}-\d{2}-\d{2}$/.test(report.version)) return suggestPeriodName(report.version);
  return report.weekLabel;
}

/** A4 가로. 72pt = 1in, 297mm × 210mm. */
export const WEEKLY_PDF_WIDTH = 841.89;
export const WEEKLY_PDF_HEIGHT = 595.28;

const MARGIN = 36;
const INK = grayscale(0.12);
const RULE = grayscale(0.75);
const WASH = grayscale(0.93);
const QUIET = grayscale(0.38);

export async function buildWeeklyPdf(report: WeeklyV2WeekSnapshot, fontBytes: Uint8Array): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  const fontkit = (await import("@pdf-lib/fontkit")).default;
  pdf.registerFontkit(fontkit);
  const font = await pdf.embedFont(fontBytes, { subset: true });
  pdf.setTitle(report.reportNo);
  pdf.setSubject("주간 보고 마감본");
  pdf.setProducer("WaveX Plan");

  const cursor = { page: pdf.addPage([WEEKLY_PDF_WIDTH, WEEKLY_PDF_HEIGHT]), y: WEEKLY_PDF_HEIGHT - MARGIN };

  const ensure = (height: number) => {
    if (cursor.y - height >= MARGIN) return;
    cursor.page = pdf.addPage([WEEKLY_PDF_WIDTH, WEEKLY_PDF_HEIGHT]);
    cursor.y = WEEKLY_PDF_HEIGHT - MARGIN;
  };

  const text = (value: string, x: number, size: number, color = INK) => {
    cursor.page.drawText(value, { x, y: cursor.y - size, size, font, color });
  };

  const rule = () => {
    cursor.page.drawLine({
      start: { x: MARGIN, y: cursor.y },
      end: { x: WEEKLY_PDF_WIDTH - MARGIN, y: cursor.y },
      thickness: 0.6,
      color: RULE,
    });
  };

  ensure(28);
  text(sheetTitle(report), MARGIN, 16);
  cursor.y -= 22;
  text(`${report.projectName}  ·  ${report.periodLabel}  ·  ${report.reportNo}`, MARGIN, 9, QUIET);
  cursor.y -= 16;
  rule();
  cursor.y -= 14;

  section(cursor, font, ensure, "공정");
  const judged = report.hasBaseline !== false;
  const verdictText = `${verdict(report.statusVerdict)}${report.verdictEdited ? " (수정)" : ""}`;
  line(cursor, font, ensure, `계획 공정  ${judged ? `${report.planProgress.toFixed(1)}%` : "—"}     실적 공정  ${report.actualProgress.toFixed(1)}%     평가  ${verdictText}`);
  line(cursor, font, ensure, report.statusSummary || "—");
  if (report.weekMemo) line(cursor, font, ensure, `평가 메모  ${report.weekMemo}`);

  if (report.sections?.cost !== false) {
    section(cursor, font, ensure, "비용 성과");
    const evm = report.costEvm ?? UNAVAILABLE_COST;
    line(cursor, font, ensure, COST_KPI.slice(0, 3).map((item) => `${item.label}  ${costKpiText(evm, item.key)}`).join("     "));
    line(cursor, font, ensure, COST_KPI.slice(3).map((item) => `${item.label}  ${costKpiText(evm, item.key)}`).join("     "));
    if (report.costNote) {
      line(cursor, font, ensure, report.costNote);
    }
  }

  section(cursor, font, ensure, "주요 업무 요약");
  if (report.prevTasks && report.prevTasks.length > 0) {
    line(cursor, font, ensure, `지난주: ${report.prevTasks.join(" · ")}`);
  }
  line(
    cursor,
    font,
    ensure,
    `금주: ${report.currentTasks.length > 0 ? report.currentTasks.join(" · ") : "이번 주 진행 작업이 없습니다."}`,
  );
  line(cursor, font, ensure, `차주: ${report.nextTasks.length > 0 ? report.nextTasks.join(" · ") : "—"}`);
  for (const ag of report.agendas) {
    line(cursor, font, ensure, `일정  ${ag.date}  ${ag.title}  ${ag.owner}  ${ag.status === "done" ? "완료" : ag.type}`);
  }

  if (report.sections?.wbs !== false) {
    section(cursor, font, ensure, "책임(A)");
    const owners = report.wbs.filter((row) => row.accountable && row.accountable !== "—");
    if (owners.length === 0) line(cursor, font, ensure, "지정된 책임이 없습니다.");
    for (const row of owners) {
      line(cursor, font, ensure, `${row.code}  ${row.name}  ${row.accountable}`);
    }
  }

  section(cursor, font, ensure, "이슈·리스크");
  if (report.issues.length === 0) line(cursor, font, ensure, "—");
  for (const issue of report.issues) {
    line(cursor, font, ensure, `${issue.code}  ${issue.title}  ${issue.owner}  ${issue.action}  ${issue.status}`);
  }

  section(cursor, font, ensure, "결정·요청");
  if (report.requests.length === 0) line(cursor, font, ensure, "—");
  for (const request of report.requests) {
    line(cursor, font, ensure, `${request.code}  ${request.title}  ${request.target}  ${request.impact}  ${request.status}`);
  }

  if (report.agingNote) {
    cursor.y -= 8;
    line(cursor, font, ensure, report.agingNote);
  }

  return pdf.save();
}

function section(
  cursor: { page: PDFPage; y: number },
  font: PDFFont,
  ensure: (height: number) => void,
  title: string,
) {
  ensure(22);
  cursor.y -= 8;
  cursor.page.drawRectangle({ x: MARGIN, y: cursor.y - 14, width: WEEKLY_PDF_WIDTH - MARGIN * 2, height: 16, color: WASH });
  cursor.page.drawText(title, { x: MARGIN + 6, y: cursor.y - 11, size: 10, font, color: INK });
  cursor.y -= 20;
}

function line(
  cursor: { page: PDFPage; y: number },
  font: PDFFont,
  ensure: (height: number) => void,
  value: string,
) {
  const width = WEEKLY_PDF_WIDTH - MARGIN * 2;
  const size = 9;
  const rows = wrap(font, value || "—", size, width);
  for (const row of rows) {
    ensure(14);
    cursor.page.drawText(row, { x: MARGIN, y: cursor.y - size, size, font, color: INK });
    cursor.y -= 13;
  }
}

function wrap(font: PDFFont, value: string, size: number, width: number): string[] {
  const rows: string[] = [];
  let current = "";
  for (const ch of value) {
    const next = current + ch;
    if (font.widthOfTextAtSize(next, size) > width && current) {
      rows.push(current);
      current = ch.trim() ? ch : "";
    } else current = next;
  }
  if (current) rows.push(current);
  return rows.length ? rows : ["—"];
}

function verdict(value: WeeklyV2WeekSnapshot["statusVerdict"]): string {
  if (value === "critical") return "위험";
  if (value === "watch") return "주의";
  if (value === "unknown") return "판정 불가";
  return "정상";
}
